import { ChildProps } from '@apollo/client/react/hoc';
import { MutationFunction } from '@reverbdotcom/commons/src/useHOCMutation';
import { gql } from '@apollo/client';
import React, { useEffect } from 'react';
import track from 'react-tracking';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

import { withRouter, WithRouterProps } from 'react-router';

import { PageSettings } from './map_params';
import { trackEvent, LISTING_TYPE } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { GridEmptyState } from './listing_grid';
import {
  CombinedMarketplaceBumpTracking,
  CoreMarketplaceCombinedMarketplaceSearch,
  Ad,
} from '../../schemas/rql';
import ErrorDisplay from '../discovery/error_display';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import * as elog from '@reverbdotcom/commons/src/elog';
import InterspersedListings from './interspersed_listings';
import { connect } from '@reverbdotcom/commons/src/connect';
import { RCListingCard, RCListingGrid } from '@reverbdotcom/cadence/components';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { Filter } from '@reverbdotcom/commons/src/components/search_filters';
import { times } from 'lodash';
import { PlaceholderListingRowCard } from '@reverbdotcom/commons/src/components/listing_row_card';
import MarketplaceGridWithSidebar from './marketplace_grid_with_sidebar';

interface MutationProps {
  mutate: MutationFunction<CombinedMarketplaceBumpTracking.Mutation, CombinedMarketplaceBumpTracking.Variables>;
}

interface IExternalProps {
  listings: CoreMarketplaceCombinedMarketplaceSearch.Listings[];
  bumpedListings: CoreMarketplaceCombinedMarketplaceSearch.Listings[];
  error: Error;
  loading: boolean;
  trackingQuery?: string;
  trackingFilter?: string;
  trackingSort?: string;
  trackingPage?: number;
  ad?: Ad;
  pageSettings?: PageSettings;
  isListView?: boolean;
  filters: Filter[];
  exposedSidebarOpen: boolean;
  useExposedSidebar: boolean;
  clearFiltersDisabled: boolean;
  toggleExposedSidebarOpen?: () => void;
  listingSearchTotal?: number;
  clearFilters?:() => void;
}

type IProps = ChildProps<
  IExternalProps & WithRouterProps & MutationProps,
  CoreMarketplaceCombinedMarketplaceSearch.Query,
  CoreMarketplaceCombinedMarketplaceSearch.Variables
>;

const COMPONENT_NAME = 'CombinedMarketplaceGrid';

export function CombinedBumpListingGrid(props: IProps) {
  const user = useUser();
  const shouldDisplayBumps = !props.loading || !props.error || !!props.bumpedListings.length;
  const bumpedListingIds = props.bumpedListings.map(l => l.id);

  useEffect(() => {
    if (shouldDisplayBumps) {
      props.mutate({
        variables: {
          input: {
            listingIds: bumpedListingIds,
          },
        },
      });

      trackEvent({
        eventName: MParticleEventName.ListingImpression,
        listingIds: bumpedListingIds,
        shownAsBumped: true,
        listings: props.listings,
        componentName: COMPONENT_NAME,
      });
    }
  }, [bumpedListingIds, props.mutate, props.listings, shouldDisplayBumps]);

  if (props.error) {
    elog.error('listing-grid-error', { ...props }, props.error);
    return <ErrorDisplay />;
  }

  function renderGridItems() {
    const noListings = props.bumpedListings.length === 0 && props.listings.length === 0;
    const isLoadingWithNoListings = props.loading && noListings;

    if (isLoadingWithNoListings) {
      return (
        <>
          {times(10, index => (
            <RCListingGrid.Item key={index} colSpan={props.isListView ? 'all' : 'one'}>
              {props.isListView
                ? <PlaceholderListingRowCard />
                : <RCListingCard.Skeleton />
              }
            </RCListingGrid.Item>
          ))}
        </>
      );
    }

    if (noListings) {
      return (
        <RCListingGrid.Item colSpan="all">
          <GridEmptyState />
        </RCListingGrid.Item>
      );
    }

    return (
      <InterspersedListings
        user={user}
        listings={props.listings}
        bumpedListings={props.bumpedListings}
        showOnlySold={props.location.query.showOnlySold}
        trackingQuery={props.trackingQuery}
        trackingFilter={props.trackingFilter}
        trackingSort={props.trackingSort}
        trackingPage={props.trackingPage}
        ad={props.ad}
        pageSettings={props.pageSettings}
        displayAsRows={props.isListView}
      />
    );
  }

  return (
    <MarketplaceGridWithSidebar
      loading={props.loading}
      filters={props.filters}
      exposedSidebarOpen={props.exposedSidebarOpen}
      useExposedSidebar={props.useExposedSidebar}
      closeSidebarFilters={props.toggleExposedSidebarOpen}
      listingSearchTotal={props.listingSearchTotal}
      clearFiltersDisabled={props.clearFiltersDisabled}
      clearFilters={props.clearFilters}
    >
      {renderGridItems()}
    </MarketplaceGridWithSidebar>
  );
}

const withMutation = withGraphql<IExternalProps, CombinedMarketplaceBumpTracking.Mutation, CombinedMarketplaceBumpTracking.Variables>(
  gql`
  mutation CombinedMarketplaceBumpTracking($input: Input_arbiter_BulkTrackImpressionsRequest) {
    bulkTrackImpressions(input: $input) {
      listingIds
    }
  }
  `,
);

export default connect<IExternalProps>([
  withRouter,
  withMutation,
  track({
    componentName: COMPONENT_NAME,
    type: LISTING_TYPE,
  }),
])(CombinedBumpListingGrid);
